<script setup lang="ts">
import { tpgIconMap } from '@tpg/grammar-web-icons';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarLocalLoader, GrammarPrimaryButton } from '@nora-monorepo/grammar-ui';

import SimpleLayerGroup from '#nora-ui/map/components/overlay-menu/groups/SimpleLayerGroup.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';

const props = defineProps<{
  mapId: string;
}>();

const { t } = useI18n();
const mapStore = useMapStore();
const map = mapStore.getMapByIdOrThrow(props.mapId);

function reloadPage(): void {
  window.location.reload();
}

const baseMaps = computed(() =>
  map.layers.getAvailableBaseMaps().map(baseMap => ({
    id: baseMap.id,
    name: baseMap.name ?? baseMap.id,
    visible: map.ref.baseMap === baseMap.id,
    toggleVisibility: () => {
      map.setBaseMap(baseMap.id);
    },
  })),
);

const mapOverlays = computed(
  () =>
    map.ref.overlayMaps.map(overlayMap => ({
      id: overlayMap.id,
      name: overlayMap.name ?? overlayMap.id,
      visible: overlayMap.visible ?? false,
      toggleVisibility: () => {
        map.toggleOverlayMapVisibility(overlayMap.id);
      },
    })),
);

const offlineMaps = computed(
  () =>
    map.ref.offlineMaps.map(offlineMap => ({
      id: offlineMap.id,
      name: offlineMap.name ?? offlineMap.id,
      visible: offlineMap.visible,
      toggleVisibility: () => {
        map.toggleOfflineMapVisibility(offlineMap.id);
      },
    })),
);
</script>

<template>
  <div
    v-if="map.layers.sourceState === 'error' || map.setupFailed"
    role="alert"
    class="mb-5 flex flex-col items-center gap-3 text-center text-gray-400"
  >
    <span>{{ t(map.layers.sourceState === 'error' ? 'mapLayers.sourceInitializationError' : 'mapLayers.mapInitializationError') }}</span>
    <GrammarPrimaryButton
      :label="t('mapLayers.reloadPage')"
      @click="reloadPage"
    />
  </div>
  <GrammarLocalLoader v-if="map.layers.sourceState === 'loading' && !map.setupFailed" />
  <div
    v-else-if="map.layers.sourceState === 'ready'"
    class="flex flex-col gap-5"
  >
    <SimpleLayerGroup
      v-if="offlineMaps.length"
      :title="t('mapLayers.offlineMaps')"
      :items="offlineMaps"
    />
    <SimpleLayerGroup
      :title="t('mapLayers.mapOverlays')"
      :items="mapOverlays"
      :empty-message="t('mapLayers.noMapOverlaysAvailable')"
      :icon="tpgIconMap"
    />
    <SimpleLayerGroup
      :title="t('mapLayers.baseMaps')"
      :items="baseMaps"
      :empty-message="t('mapLayers.noBaseMapsAvailable')"
      :icon="tpgIconMap"
      :is-radio-button="true"
      item-class="pb-1"
    />
  </div>
</template>
